import React, { ReactNode, useMemo, useContext, useCallback, useRef, forwardRef, ComponentType } from 'react';
import { ThemeContext } from 'styled-components';

import changeLanguage from 'ia-react-core/~/actions/fn/changeLanguage';
import Promo2 from 'ia-react-core/~/components/titles/Promo2';
import T2 from 'ia-react-core/~/components/titles/T2';
import { FooterLinksProps } from 'ia-react-core/~/components/layout/FooterLinks';
import COLOR_SCHEMES from '../../constants/COLOR_SCHEMES';

import findChildByType from '../../utils/components/#findChildByType';
import filterChildrenByType from '../../utils/components/#filterChildrenByType';
import excludeChildrenByType from '../../utils/components/#excludeChildrenByType';
import setRef from '../../utils/components/#setRef';

import ColorSchemeContext from '../../components/colorScheme/ColorSchemeContext';

import MainHeader from '../../components/layout/MainHeader';
import AppHeader from '../../components/layout/AppHeader';
import AppFooter from '../../components/layout/AppFooter';
import MainFooter from '../../components/layout/MainFooter';

import { LayoutGlobalStyles, StyledApplicationLayout, StyledMainContent } from './ApplicationLayout.styles';
import { Content, Footer, BeforeHeader } from './ApplicationLayout.compounds';

export interface ApplicationLayoutProps {
  /** The layout content */
  children?: ReactNode;
  /** Removes the content's padding */
  nopadding?: boolean;
  /** Overrides the copyright text. */
  copyrightText?: ReactNode;
  /** Removes the app header */
  noAppHeader?: boolean;
  /** Header height */
  headerHeight?: number;
  /** Removes the main footer */
  noMainFooter?: boolean;
  /** Removes the main header */
  noMainHeader?: boolean;
  /** Removes the logo in the header */
  noHeaderLogo?: boolean;
  /** Lets you modify the home URL that wrap the main logo */
  homePageUrl?: string;
  /** Removes the language button from the header */
  noHeaderLanguageButton?: boolean;
  /** headerPrimary */
  headerPrimary?: boolean;
  /** Use the horizontal variant of the logo. */
  horizontalLogo?: boolean;
  /** Put the language button on the same row as the slogan */
  languageButtonInlineWithSlogan?: boolean;
  /** Removes the language button from the footer */
  noFooterLanguageButton?: boolean;
  /** Links for the Sitecore service returning the footer links, */
  mainFooterLinksUrl?: string;
  /** The url of the footer links when it is not specified. */
  mainFooterLinksDomain?: string;
  /** List of external links for the footer. Prefer `mainFooterLinksUrl` when possible. */
  mainFooterLinks?: FooterLinksProps['links'];
  /** List of buttons to display to the right of the links. */
  mainFooterButtons?: {
    onClick: () => void;
    RightIcon: any;
    text: ReactNode;
    props?: Object;
  }[];
  /** 'background' style of the content */
  contentBackground?: string;
  /** 'background' style of the header */
  headerBackground?: string;
  /** App name to display next to the logo. */
  appName?: string;
  /** 'background-repeat' style of the content */
  contentBackgroundRepeat?: string;
  /** 'background-size' style of the content */
  contentBackgroundSize?: string;
  /** 'background-position' style of the content */
  contentBackgroundPosition?: string;
  /** 'background-attachment' style of the content */
  contentBackgroundAttachment?: string;
  /** Minimum width of the content. */
  minContentWidth?: number;
  /** Maximum width of the content. */
  maxContentWidth?: number;
  /** Vertical padding of the content. */
  contentVerticalPadding?: number;
  /** Height css of the content */
  contentHeight?: string;
  /** Min height of the layout */
  minHeight?: string;
    /** Title type to used in PrimaryPage. */
  PrimaryTitle?: ComponentType<{ children: ReactNode }>;
  /** Title type to used in SecondaryPage. */
  SecondaryTitle?: ComponentType<{ children: ReactNode }>;
  /** Callback when the X button is clicked. The button is only displayedif this option is defined. */
  onClose?: () => void;
}

export interface ApplicationLayoutInternalsProps {
  dispatchChangeLanguage: typeof changeLanguage;
}

export interface ApplicationLayoutComposedProps extends ApplicationLayoutProps, ApplicationLayoutInternalsProps {}

export type ApplicationLayoutStatics = {
  BeforeHeader: typeof BeforeHeader;
  Content: typeof Content;
  Footer: typeof Footer;
  MainHeaderRightContent: typeof MainHeader.RightContent;
  MainHeaderMiddleContent: typeof MainHeader.MiddleContent;
};

const ApplicationLayoutDefaultProps: ApplicationLayoutProps = {
  PrimaryTitle: Promo2,
  SecondaryTitle: T2,
  minHeight: '100vh',
};

/**
 * @since 0.18.0
 */
const ApplicationLayout = Object.assign(
  forwardRef<HTMLDivElement, ApplicationLayoutComposedProps>(({
    children, copyrightText, noAppHeader, horizontalLogo, nopadding, noMainFooter, noMainHeader, homePageUrl, mainFooterLinks, mainFooterLinksUrl, mainFooterLinksDomain, mainFooterButtons, noHeaderLanguageButton, noFooterLanguageButton, onClose, dispatchChangeLanguage, contentBackground, headerBackground, contentBackgroundRepeat, contentBackgroundSize, appName, noHeaderLogo, headerHeight, minContentWidth, maxContentWidth, headerPrimary, contentVerticalPadding, contentBackgroundPosition, contentBackgroundAttachment, contentHeight, PrimaryTitle, SecondaryTitle, languageButtonInlineWithSlogan, minHeight, ...props
  }, ref) => {
    const beforeHeader = findChildByType(children, BeforeHeader);
    const mainHeaderRightContent = findChildByType(children, MainHeader.RightContent);
    const mainHeaderMiddleContent = findChildByType(children, MainHeader.MiddleContent);
    const footer = filterChildrenByType(children, Footer);
    const hasAppFooter = Boolean(footer && footer.length);
    const content = excludeChildrenByType(children, BeforeHeader, Footer, MainHeader.RightContent, MainHeader.MiddleContent);
    const footerColorScheme = footer.length ? COLOR_SCHEMES.SHADE : COLOR_SCHEMES.LIGHT;

    const layoutRef = useRef<HTMLDivElement>();

    const mergeRefs = useCallback((current: HTMLDivElement) => {
      setRef(ref, current);
      setRef(layoutRef, current);
    }, [ref]);

    const theme = useContext(ThemeContext);

    const resolvedNoAppHeader = useMemo(() => {
      if (typeof noAppHeader !== 'undefined') {
        return noAppHeader;
      }

      return theme.layout.noAppHeader;
    }, [noAppHeader, theme.layout.noAppHeader]);

    const handleFooterLanguageChange = useCallback((nextLanguage: string) => {
      dispatchChangeLanguage(nextLanguage);
      layoutRef?.current.scrollTo(0, 0);
    }, [dispatchChangeLanguage]);

    const colorSchemeContext = useMemo(() => ({ scheme: footerColorScheme }), [footerColorScheme]);

    return (
      <>
        <LayoutGlobalStyles />
        <StyledApplicationLayout
          ref={mergeRefs}
          $minHeight={minHeight}
          {...props}
        >
          {beforeHeader}
          {!noMainHeader && (
            <MainHeader
              primary={headerPrimary}
              horizontalLogo={horizontalLogo}
              appName={appName}
              homePageUrl={homePageUrl}
              noLanguageButton={noHeaderLanguageButton}
              onLanguageChange={dispatchChangeLanguage}
              noLogo={noHeaderLogo}
              onClose={onClose}
              height={headerHeight}
              background={headerBackground}
            >
              {mainHeaderMiddleContent}
              {mainHeaderRightContent}
            </MainHeader>
          )}
          {!resolvedNoAppHeader && <AppHeader />}
          <StyledMainContent
            appName={appName}
            PrimaryTitle={PrimaryTitle}
            SecondaryTitle={SecondaryTitle}
            nopadding={nopadding}
            background={contentBackground}
            backgroundRepeat={contentBackgroundRepeat}
            backgroundSize={contentBackgroundSize}
            minContentWidth={minContentWidth}
            maxContentWidth={maxContentWidth}
            verticalPadding={contentVerticalPadding}
            backgroundPosition={contentBackgroundPosition}
            backgroundAttachment={contentBackgroundAttachment}
            contentHeight={contentHeight}
          >
            {content}
          </StyledMainContent>
          <ColorSchemeContext.Provider value={colorSchemeContext}>
            {hasAppFooter && (
              <AppFooter>
                {footer}
              </AppFooter>
            )}
            {!noMainFooter && (
              <MainFooter
                copyrightText={copyrightText}
                linksUrl={mainFooterLinksUrl}
                linksDomain={mainFooterLinksDomain}
                links={mainFooterLinks}
                buttons={mainFooterButtons}
                noLanguageButton={noFooterLanguageButton}
                onLanguageChange={handleFooterLanguageChange}
                languageButtonInlineWithSlogan={languageButtonInlineWithSlogan}
              />
            )}
          </ColorSchemeContext.Provider>
        </StyledApplicationLayout>
      </>
    );
  }),
  {
    BeforeHeader,
    Content,
    Footer,
    MainHeaderRightContent: MainHeader.RightContent,
    MainHeaderMiddleContent: MainHeader.MiddleContent,
  },
);

ApplicationLayout.defaultProps = ApplicationLayoutDefaultProps;

export default ApplicationLayout;
